import { router } from "@inertiajs/react"
import { Archive, Pause, Play } from "lucide-react"
import type { ComponentType, SVGProps } from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { Spinner } from "@/components/ui/spinner"
import { useIsMobile } from "@/hooks/use-mobile"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"
import type { CrewMemberActions, CrewMembershipStatus } from "@/types/crew"

/**
 * OKS-26 — Action bar for the creator detail page (UX Spec V2 §Action
 * bar). Port-adapted from OKS-19 `<CandidateActionBar>` with three
 * differences:
 *
 *   - 3-button action set (`pause` / `unpause` / `archive`) replacing
 *     OKS-19's 8-button funnel.
 *   - **Hide-when-null** the SINGLE visibility discriminator
 *     (`actions.<verb>_url === null → button DOM-absent`); preserved
 *     from OKS-19.
 *   - **Archive is terminal — confirmation REQUIRED.** shadcn
 *     `AlertDialog` (≥640px) / `Drawer` (<640px); default focus on
 *     destructive `Archive` button; ESC + click-outside cancel; focus
 *     restored to the Archive trigger on close.
 *   - **Button-local in-flight spinner** with `aria-busy="true"` on the
 *     clicked button (UX V2 lock — NO full-page overlay during
 *     transitions).
 *   - **FR `Pause` Anglicism** (UX override) — i18n key
 *     `crew.detail.actions.pause` ships as `Pause` in FR (not
 *     `Mettre en pause`) to avoid 375px sticky-footer overflow.
 *
 * **Wire format** (BE TRD rev-5 lock — nested strong-params):
 *   `router.post(actions.<verb>_url, {transition: {event}})`
 * with `event ∈ "pause" | "unpause" | "archive"`. Forged POSTs hit
 * `policy.transition?(event:)` server-side → 404 (defence-in-depth).
 *
 * **Optimistic badge swap** is wired via the `onOptimisticTransition`
 * callback — parent owns the badge state. Errors in `onError` invoke
 * `onOptimisticRevert` to roll back. UX V2 §Micro-interactions.
 *
 * **Archive redirects** to `/crew` (303 from BE); FE renders flash on
 * the roster page after — no in-page success toast needed here.
 *
 * Locked testids:
 *   - `creator-detail-action-bar` — root (QA matrix scope root)
 *   - `creator-detail-action-pause`
 *   - `creator-detail-action-unpause`
 *   - `creator-detail-action-archive`
 *   - `creator-detail-archive-dialog`
 *   - `creator-detail-archive-confirm`
 *   - `creator-detail-archive-cancel`
 */

export interface CrewMemberActionBarProps {
  actions: CrewMemberActions
  /** Page-subject identity — used in dialog copy. */
  handle: string
  /**
   * Badge swap callbacks. Parent owns the optimistic status state so
   * the header's `<CrewMemberStatusBadge>` re-renders without a
   * router roundtrip. Pass `null` to opt out of optimism.
   */
  onOptimisticTransition?: (next: CrewMembershipStatus) => void
  onOptimisticRevert?: () => void
  className?: string
}

type DispatcherKind = "post" | "modal-archive"

interface ButtonDef {
  urlKey: keyof CrewMemberActions
  testId: string
  labelKey: string
  variant: "default" | "outline" | "destructive"
  /** BE-locked event string posted in `{transition: {event}}`. */
  event: "pause" | "unpause" | "archive"
  icon: ComponentType<SVGProps<SVGSVGElement>>
  kind: DispatcherKind
  /** Optimistic status target; null when no badge swap (e.g. archive → redirect). */
  optimisticStatus: CrewMembershipStatus | null
}

/**
 * Reading order: destructive (leftmost) · corrective (middle) ·
 * forward-primary (rightmost). Encoded as iteration order; per-state
 * at most 3 buttons render (fits inline at every breakpoint).
 */
const BUTTON_DEFS: readonly ButtonDef[] = [
  {
    urlKey: "archive_url",
    testId: "creator-detail-action-archive",
    labelKey: "crew.detail.actions.archive",
    variant: "destructive",
    event: "archive",
    icon: Archive,
    kind: "modal-archive",
    // No optimistic swap — page redirects to /crew on success.
    optimisticStatus: null,
  },
  {
    urlKey: "pause_url",
    testId: "creator-detail-action-pause",
    labelKey: "crew.detail.actions.pause",
    variant: "outline",
    event: "pause",
    icon: Pause,
    kind: "post",
    optimisticStatus: "paused",
  },
  {
    urlKey: "unpause_url",
    testId: "creator-detail-action-unpause",
    labelKey: "crew.detail.actions.unpause",
    variant: "default",
    event: "unpause",
    icon: Play,
    kind: "post",
    optimisticStatus: "active",
  },
]

export function CrewMemberActionBar({
  actions,
  handle,
  onOptimisticTransition,
  onOptimisticRevert,
  className,
}: CrewMemberActionBarProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const isMobile = useIsMobile()
  /** Currently in-flight event (for button-local spinner). Single transition at a time. */
  const [inFlight, setInFlight] = useState<ButtonDef["event"] | null>(null)
  const [archiveOpen, setArchiveOpen] = useState(false)

  const visibleButtons = BUTTON_DEFS.filter(
    (def) => actions[def.urlKey] !== null,
  )

  // UX in-flight review #2 — when zero actions are legal (archived
  // terminal state), drop the empty wrapper entirely so it doesn't
  // occupy header / sticky-footer space. The Archive dialog state is
  // closed in this branch by definition (no archive_url to open it).
  if (visibleButtons.length === 0) {
    return null
  }

  function dispatchPost(def: ButtonDef) {
    const url = actions[def.urlKey]
    if (!url || inFlight !== null || !online) return
    if (def.optimisticStatus !== null) {
      onOptimisticTransition?.(def.optimisticStatus)
    }
    setInFlight(def.event)
    router.post(
      url,
      { transition: { event: def.event } },
      {
        preserveScroll: true,
        onError: () => {
          if (def.optimisticStatus !== null) {
            onOptimisticRevert?.()
          }
        },
        onFinish: () => setInFlight(null),
      },
    )
  }

  function handleClick(def: ButtonDef) {
    if (def.kind === "modal-archive") {
      setArchiveOpen(true)
      return
    }
    dispatchPost(def)
  }

  function submitArchive() {
    const url = actions.archive_url
    if (!url || inFlight !== null || !online) return
    setInFlight("archive")
    router.post(
      url,
      { transition: { event: "archive" } },
      {
        preserveScroll: false,
        onSuccess: () => {
          setArchiveOpen(false)
        },
        onFinish: () => setInFlight(null),
      },
    )
  }

  return (
    <div
      data-testid="creator-detail-action-bar"
      className={cn(
        // ≥640px: inline horizontal bar; <640px: sticky footer.
        "flex items-center gap-2",
        // Sticky-footer behavior at <640px is composed by the
        // page-level wrapper (which adds container padding); this
        // component just lays out the buttons.
        className,
      )}
    >
      {visibleButtons.map((def) => {
        const Icon = def.icon
        const isThisInFlight = inFlight === def.event
        const isOtherInFlight = inFlight !== null && !isThisInFlight
        const isDisabled = isOtherInFlight || !online
        return (
          <Button
            key={def.urlKey}
            type="button"
            variant={def.variant}
            data-testid={def.testId}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            aria-busy={isThisInFlight}
            onClick={() => handleClick(def)}
          >
            {isThisInFlight ? (
              <Spinner className="size-4" />
            ) : (
              <Icon className="size-4" aria-hidden="true" />
            )}
            {t(def.labelKey)}
          </Button>
        )
      })}

      {/*
        Archive confirmation — AlertDialog ≥640px / Drawer <640px.
        Default focus on destructive `Archive` button. ESC +
        click-outside cancel; focus trap inherited from Radix.
        Locked testids: `creator-detail-archive-dialog` (Content) +
        `creator-detail-archive-confirm` + `creator-detail-archive-cancel`.
      */}
      {actions.archive_url !== null && isMobile && (
        <Drawer open={archiveOpen} onOpenChange={setArchiveOpen}>
          <DrawerContent data-testid="creator-detail-archive-dialog">
            <DrawerHeader>
              <DrawerTitle>
                {t("crew.detail.actions.archive_dialog_title", { handle })}
              </DrawerTitle>
              <DrawerDescription>
                {t("crew.detail.actions.archive_dialog_description")}
              </DrawerDescription>
            </DrawerHeader>
            <DrawerFooter>
              <Button
                type="button"
                variant="destructive"
                onClick={submitArchive}
                disabled={inFlight !== null || !online}
                aria-busy={inFlight === "archive"}
                autoFocus
                data-testid="creator-detail-archive-confirm"
              >
                {inFlight === "archive" && <Spinner className="size-4" />}
                {t("crew.detail.actions.archive_dialog_confirm")}
              </Button>
              <DrawerClose
                disabled={inFlight !== null}
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    data-testid="creator-detail-archive-cancel"
                  />
                }
              >
                {t("crew.detail.actions.archive_dialog_cancel")}
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
      {actions.archive_url !== null && !isMobile && (
        <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
          <AlertDialogContent data-testid="creator-detail-archive-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("crew.detail.actions.archive_dialog_title", { handle })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("crew.detail.actions.archive_dialog_description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={inFlight !== null}
                data-testid="creator-detail-archive-cancel"
              >
                {t("crew.detail.actions.archive_dialog_cancel")}
              </AlertDialogCancel>
              {/* OKS-1023 — plain `Button`, not `AlertDialogAction` (Base UI
                  `Dialog.Close` closes on click whatever the handler does).
                  The dialog holds with the button-local spinner until the BE
                  responds; `submitArchive` closes it on `onSuccess`. */}
              <Button
                type="button"
                onClick={submitArchive}
                disabled={inFlight !== null || !online}
                aria-busy={inFlight === "archive"}
                autoFocus
                data-testid="creator-detail-archive-confirm"
                className="bg-destructive hover:bg-destructive/90 text-white"
              >
                {inFlight === "archive" && <Spinner className="size-4" />}
                {t("crew.detail.actions.archive_dialog_confirm")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  )
}
